import { ValidationError } from '@goodt-common/domain';
import { MessageI18n } from '@goodt-common/utils';
import { queryFilterExists, QueryModelValidatorMessages, querySortExists } from '@goodt-common/data';
import { EntityType } from '../../../const';

/**
 * @typedef {typeof import('../../../const').EntityType} EntityTypeEnum
 */

export class QueryModelValidator {
    constructor({ queryModel, datasetInfo }) {
        this.queryModel = queryModel;
        this.datasetInfo = datasetInfo;
    }

    /**
     *
     * @param {EntityTypeEnum[keyof EntityTypeEnum]} type
     * @return {string[]}
     * @private
     */
    _getQueryMissedParams(type) {
        const { queryModel, datasetInfo } = this;

        if (type === EntityType.METRIC) {
            const datasetMetrics = datasetInfo.metrics.map(({ name }) => name);
            return queryModel.metrics.filter((name) => datasetMetrics.includes(name) === false);
        }

        if (type === EntityType.DIMENSION) {
            const datasetDimensions = datasetInfo.dimensions.map(({ name }) => name);
            return queryModel.dimensions.filter((name) => datasetDimensions.includes(name) === false);
        }

        if (type === EntityType.VARIABLE) {
            const datasetVariables = datasetInfo.variables.map(({ name }) => name);
            return queryModel.variables
                .filter(({ name }) => datasetVariables.includes(name) === false)
                .map(({ name }) => name);
        }

        return [];
    }

    validateMetrics() {
        const metrics = this._getQueryMissedParams(EntityType.METRIC);
        return metrics.map((name) => {
            const message = new MessageI18n(QueryModelValidatorMessages.METRICS_MISSED, {
                name
            });
            return new ValidationError(message, {
                [EntityType.METRIC]: {
                    [name]: {
                        message
                    }
                }
            });
        });
    }

    validateVariables() {
        const variables = this._getQueryMissedParams(EntityType.VARIABLE);
        return variables.map((name) => {
            const message = new MessageI18n(QueryModelValidatorMessages.VARIABLES_MISSED, {
                name: `@${name}`
            });
            return new ValidationError(message, {
                [EntityType.VARIABLE]: {
                    [name]: {
                        message
                    }
                }
            });
        });
    }

    validateDimensions() {
        const dimensions = this._getQueryMissedParams(EntityType.DIMENSION);
        return dimensions.map((name) => {
            const message = new MessageI18n(QueryModelValidatorMessages.DIMENSIONS_MISSED, {
                name
            });
            return new ValidationError(message, {
                [EntityType.DIMENSION]: {
                    [name]: {
                        message
                    }
                }
            });
        });
    }

    validateFilters() {
        const { queryModel: { raw: query } } = this;
        const dimensions = this._getQueryMissedParams(EntityType.DIMENSION);
        const metrics = this._getQueryMissedParams(EntityType.METRIC);
        return [...metrics, ...dimensions]
            .filter((name) => queryFilterExists({ query, filter: name }))
            .map((name) => {
                const template = metrics.includes(name)
                    ? QueryModelValidatorMessages.METRICS_MISSED
                    : QueryModelValidatorMessages.DIMENSIONS_MISSED;
                const message = new MessageI18n(template, {
                    name
                });
                return new ValidationError(message, {
                    [EntityType.FILTER]: {
                        [name]: {
                            message
                        }
                    }
                });
            });
    }

    validateSort() {
        const { queryModel: { raw: query } } = this;
        const dimensions = this._getQueryMissedParams(EntityType.DIMENSION);
        const metrics = this._getQueryMissedParams(EntityType.METRIC);
        return [...metrics, ...dimensions]
            .filter((name) => querySortExists({ query, sort: name }))
            .map((name) => {
                const template = metrics.includes(name)
                    ? QueryModelValidatorMessages.METRICS_MISSED
                    : QueryModelValidatorMessages.DIMENSIONS_MISSED;
                const message = new MessageI18n(template, {
                    name
                });
                return new ValidationError(message, {
                    [EntityType.SORT]: {
                        [name]: {
                            message
                        }
                    }
                });
            });
    }

    validateDrilldowns() {
        const { queryModel } = this;
        const missedDimensions = this._getQueryMissedParams(EntityType.DIMENSION);
        return queryModel.drilldowns.reduce((acc, { name, dimensions }) => {
            const missedDrilldownDimensions = missedDimensions.filter((dimension) => dimensions.includes(dimension));
            if (missedDrilldownDimensions.length === 0) {
                return acc;
            }
            const message = new MessageI18n(QueryModelValidatorMessages.DRILLDOWN_DIMENSIONS_MISSED, {
                name,
                dimensions: missedDrilldownDimensions.join(', ')
            });
            const error = new ValidationError(message, {
                [EntityType.DRILLDOWN]: {
                    [name]: { message }
                }
            });
            return [...acc, error];
        }, []);
    }

    validate() {
        return [
            ...this.validateDimensions(),
            ...this.validateMetrics(),
            ...this.validateVariables(),
            ...this.validateDrilldowns(),
            ...this.validateFilters(),
            ...this.validateSort()
        ];
    }
}
